// Shared vehicle-financing math — the one formula this app trusts for a
// monthly payment. Three consumers: VehicleRow.jsx's own inline estPayment
// calc (not yet migrated to call this — same formula, kept separate there
// for now), Clyde's calculate_loan tool (server-side mirror at
// functions/_lib/loanMath.js, kept in sync deliberately — Cloudflare Pages
// Functions don't share a module graph with the CRA-bundled src/ tree, same
// reasoning as valuation.js's simulateValue mirror of
// functions/_lib/enrichFinancials.js), and the four-square deal worksheet.
//
// All functions are pure — no imports, no I/O — specifically so the server
// mirror can stay a byte-for-byte copy with nothing to diverge accidentally.

// Standard amortized monthly payment for a principal financed at a fixed
// APR over termMonths. 0% APR falls back to a flat principal/term split
// (the amortization formula divides by zero at r=0) — same handling the
// old HomeFleet_BuyingFlow.jsx calcPayment already used.
export function paymentForLoan(principal, annualRatePct, termMonths) {
  if (!principal || principal <= 0 || !termMonths || termMonths <= 0) return 0;
  if (!annualRatePct) return Math.round(principal / termMonths);
  const r = annualRatePct / 100 / 12;
  return Math.round((principal * r) / (1 - Math.pow(1 + r, -termMonths)));
}

// Algebraic inverse of paymentForLoan — the largest principal serviceable
// by a given target monthly payment. Exact, not iterative: this is a
// straight rearrangement of the same formula, not a solve for rate or term
// (neither of which this app ever needs to infer).
export function principalForPayment(monthlyPayment, annualRatePct, termMonths) {
  if (!monthlyPayment || monthlyPayment <= 0 || !termMonths || termMonths <= 0) return 0;
  if (!annualRatePct) return Math.round(monthlyPayment * termMonths);
  const r = annualRatePct / 100 / 12;
  return Math.round((monthlyPayment * (1 - Math.pow(1 + r, -termMonths))) / r);
}

export function loanTotals(principal, annualRatePct, termMonths) {
  const monthlyPayment = paymentForLoan(principal, annualRatePct, termMonths);
  const totalCost = monthlyPayment * termMonths;
  const totalInterest = Math.max(0, totalCost - principal);
  return { monthlyPayment, totalInterest, totalCost };
}

// The single entry point both Clyde's tool and the worksheet's Financing
// pane call — bidirectional so the caller never has to know which formula
// to reach for. Pass `price`, get the payment; pass `monthlyPayment`
// instead (omit price), get the max affordable price. Passing both is
// treated as `price` taking precedence, since a stated price is a harder
// constraint than a target payment.
export function solveLoan({ price, downPayment = 0, annualRatePct, termMonths, monthlyPayment }) {
  if (price != null) {
    const financedAmount = Math.max(0, price - downPayment);
    const totals = loanTotals(financedAmount, annualRatePct, termMonths);
    return { price, downPayment, financedAmount, ...totals };
  }
  if (monthlyPayment != null) {
    const financedAmount = principalForPayment(monthlyPayment, annualRatePct, termMonths);
    const totals = loanTotals(financedAmount, annualRatePct, termMonths);
    return { price: financedAmount + downPayment, downPayment, financedAmount, ...totals };
  }
  throw new Error("solveLoan needs either price or monthlyPayment");
}

// The worksheet's Add-ons pane needs a *differential* — what one add-on
// actually costs financed over the term, not its sticker price — not a
// second full loan calculation. Reuses loanTotals on the add-on's price in
// isolation rather than introducing a fourth formula.
export function addOnFinancedCost(addOnPrice, annualRatePct, termMonths) {
  const { monthlyPayment, totalCost } = loanTotals(addOnPrice, annualRatePct, termMonths);
  return { monthlyDelta: monthlyPayment, totalFinancedCost: totalCost };
}
